// pages/ApnaClinic/Pbx4455Sync.jsx
// Operational dashboard for WEBDOC APNA Clinic (PBX 4455) sync.
// UI follows Dialer Recording / Cron Jobs. Does not change recording cron.

import React, { useCallback, useEffect, useRef, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import PageLoader from "../../components/Loader/PageLoader";
import * as api from "../../api/apnaClinic4455Api";
import "./Pbx4455Sync.css";

const DEFAULT_TZ = "Asia/Karachi";
const POLL_MS = 15000;
const RUNS_LIMIT = 20;
const ITEMS_LIMIT = 100;

const TIMEZONES = [
  "Asia/Karachi",
  "UTC",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Riyadh",
  "Europe/London",
  "America/New_York",
];

function pick(obj, keys, fallback = null) {
  if (!obj || typeof obj !== "object") return fallback;
  for (const key of keys) {
    const v = obj[key];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
}

function unwrapObject(body) {
  if (!body || typeof body !== "object") return {};
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return body.data;
  }
  if (body.config && typeof body.config === "object") return body.config;
  if (body.run && typeof body.run === "object") return body.run;
  return body;
}

function unwrapList(body) {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  const d = body.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.rows)) return d.rows;
  if (Array.isArray(d?.runs)) return d.runs;
  if (Array.isArray(body.items)) return body.items;
  if (Array.isArray(body.rows)) return body.rows;
  if (Array.isArray(body.runs)) return body.runs;
  return [];
}

function unwrapTotal(body, fallback) {
  const d = body?.data;
  return (
    d?.total ??
    d?.totalRecords ??
    d?.count ??
    body?.total ??
    body?.totalRecords ??
    fallback
  );
}

function parseConfig(body) {
  const raw = unwrapObject(body);
  const enabledRaw = pick(raw, ["enabled", "is_enabled"], false);
  return {
    enabled: enabledRaw === true || enabledRaw === 1 || enabledRaw === "true",
    interval_minutes: Number(pick(raw, ["interval_minutes", "intervalMinutes"], 15)),
    timezone: String(pick(raw, ["timezone", "time_zone", "tz"], DEFAULT_TZ)),
    updated_at: pick(raw, ["updated_at", "updatedAt", "last_updated", "lastUpdated"], null),
  };
}

function parseRun(row) {
  if (!row || typeof row !== "object") return null;
  const nested = row.run && typeof row.run === "object" ? row.run : row;
  return {
    id: pick(nested, ["id", "run_id", "runId"], null),
    status: String(pick(nested, ["status"], "unknown")),
    trigger: pick(nested, ["trigger", "triggered_by", "source_trigger"], "—"),
    dry_run: Boolean(pick(nested, ["dry_run", "dryRun", "is_dry_run"], false)),
    source_count: pick(nested, ["source_count", "sourceCount", "source"], null),
    matched: pick(nested, ["matched", "matched_count", "matchedCount"], null),
    inserted: pick(nested, ["inserted", "inserted_count", "insertedCount"], null),
    skipped: pick(nested, ["skipped", "skipped_count", "skippedCount"], null),
    failed: pick(nested, ["failed", "failed_count", "failedCount"], null),
    started_at: pick(nested, ["started_at", "startedAt", "created_at", "createdAt"], null),
    finished_at: pick(nested, ["finished_at", "finishedAt", "ended_at", "endedAt"], null),
    error: pick(nested, ["error", "run_error", "error_message", "errorMessage", "message"], null),
  };
}

function parseItem(row) {
  return {
    lead_id: pick(row, ["lead_id", "leadId", "id"], "—"),
    phone: pick(row, ["phone", "phone_number", "phoneNumber"], "—"),
    outcome: pick(row, ["outcome", "status"], "—"),
    destination_id: pick(row, ["destination_id", "destinationId", "dest_id"], "—"),
    message: pick(row, ["message", "error", "detail", "reason"], "—"),
  };
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-PK", {
    timeZone: DEFAULT_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

function countLabel(v) {
  return v === null || v === undefined ? "—" : String(v);
}

function isIanaTimezone(tz) {
  const value = String(tz || "").trim();
  if (!value) return false;
  try {
    Intl.DateTimeFormat("en-US", { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function Alert({ type, message, onClose }) {
  if (!message) return null;
  const styles = {
    success: "alert alert-success",
    danger: "alert alert-danger",
    warning: "alert alert-warning",
    info: "alert alert-info",
  };
  const icon =
    type === "success"
      ? "bi-check-circle-fill"
      : type === "danger"
        ? "bi-x-circle-fill"
        : type === "warning"
          ? "bi-exclamation-triangle-fill"
          : "bi-info-circle-fill";
  return (
    <div
      className={`${styles[type] || "alert alert-info"} alert-dismissible fade show d-flex align-items-center gap-2 pbx4455-toast`}
      role="alert"
    >
      <i className={`bi ${icon}`} aria-hidden="true" />
      <span className="pbx4455-error-text">{message}</span>
      {onClose ? (
        <button type="button" className="btn-close ms-auto" aria-label="Dismiss" onClick={onClose} />
      ) : null}
    </div>
  );
}

function ConfirmModal({ open, title, message, confirmLabel = "Confirm", danger, busy, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="pbx4455-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="pbx4455-confirm-title">
      <div className="pbx4455-modal card border-0 shadow">
        <div className="card-body p-4">
          <h5 id="pbx4455-confirm-title" className="fw-bold mb-2">
            {title}
          </h5>
          <p className="text-muted mb-4">{message}</p>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className={`btn btn-sm ${danger ? "btn-danger" : "btn-primary"}`}
              onClick={onConfirm}
              disabled={busy}
            >
              {busy ? <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" /> : null}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const raw = String(status || "unknown").toLowerCase();
  const map = {
    completed: { cls: "badge-status-completed", icon: "bi-check-circle-fill", label: "Completed" },
    success: { cls: "badge-status-success", icon: "bi-check-circle-fill", label: "Success" },
    running: { cls: "badge-status-running", icon: "bi-arrow-repeat", label: "Running" },
    pending: { cls: "badge-status-pending", icon: "bi-hourglass-split", label: "Pending" },
    failed: { cls: "badge-status-failed", icon: "bi-x-circle-fill", label: "Failed" },
    error: { cls: "badge-status-error", icon: "bi-x-circle-fill", label: "Error" },
    paused: { cls: "badge-status-paused", icon: "bi-pause-circle-fill", label: "Paused" },
    enabled: { cls: "badge-status-enabled", icon: "bi-play-circle-fill", label: "Enabled" },
    disabled: { cls: "badge-status-disabled", icon: "bi-pause-circle-fill", label: "Paused" },
  };
  const s = map[raw] || { cls: "badge-status-pending", icon: "bi-info-circle", label: status || "Unknown" };
  return (
    <span className={`badge-status ${s.cls}`}>
      <i className={`bi ${s.icon} me-1 ${raw === "running" ? "spin-icon" : ""}`} aria-hidden="true" />
      {s.label}
    </span>
  );
}

function EmptyState({ icon = "bi-inbox", title, hint }) {
  return (
    <div className="pbx4455-empty text-center py-5 text-muted">
      <i className={`bi ${icon} fs-1 d-block mb-2`} aria-hidden="true" />
      <div className="fw-semibold">{title}</div>
      {hint ? <div className="small mt-1">{hint}</div> : null}
    </div>
  );
}

function Pagination({ page, total, limit, onPage, disabled }) {
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / limit) || 1);
  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
      <small className="text-muted">
        Page {page} of {totalPages}
        {total ? ` · ${total} total` : ""}
      </small>
      <div className="btn-group">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={disabled || page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Previous
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={disabled || page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function validateSchedule(intervalMinutes, timezone) {
  const n = Number(intervalMinutes);
  if (!Number.isInteger(n) || n < 1 || n > 1440) {
    return "Interval must be a whole number between 1 and 1440 minutes.";
  }
  if (!isIanaTimezone(timezone)) {
    return "Timezone must be a valid IANA name (for example Asia/Karachi).";
  }
  return null;
}

export default function Pbx4455Sync() {
  const [config, setConfig] = useState(null);
  const [intervalMinutes, setIntervalMinutes] = useState(15);
  const [timezone, setTimezone] = useState(DEFAULT_TZ);
  const [configLoading, setConfigLoading] = useState(true);
  const [connectionFailure, setConnectionFailure] = useState(null);

  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsPage, setRunsPage] = useState(1);
  const [runsLoading, setRunsLoading] = useState(true);

  const [selectedRunId, setSelectedRunId] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsPage, setItemsPage] = useState(1);
  const [outcomeFilter, setOutcomeFilter] = useState("failed");
  const [itemsLoading, setItemsLoading] = useState(false);

  const [busyKey, setBusyKey] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [tabVisible, setTabVisible] = useState(
    typeof document === "undefined" ? true : document.visibilityState !== "hidden"
  );

  const selectedRunIdRef = useRef(selectedRunId);
  selectedRunIdRef.current = selectedRunId;

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
  }, []);

  const loadConfig = useCallback(async ({ silent } = {}) => {
    if (!silent) setConfigLoading(true);
    try {
      const body = await api.getConfig();
      const parsed = parseConfig(body);
      setConfig(parsed);
      setIntervalMinutes(parsed.interval_minutes || 15);
      setTimezone(parsed.timezone || DEFAULT_TZ);
      setConnectionFailure(null);
    } catch (err) {
      if (err.connectionFailure) setConnectionFailure(err.message);
      else showAlert("danger", err.message || "Could not load config.");
    } finally {
      if (!silent) setConfigLoading(false);
    }
  }, [showAlert]);

  const loadRuns = useCallback(
    async (page = 1, { silent } = {}) => {
      if (!silent) setRunsLoading(true);
      try {
        const body = await api.getRuns({ page, limit: RUNS_LIMIT });
        const list = unwrapList(body).map(parseRun).filter(Boolean);
        setRuns(list);
        setRunsTotal(unwrapTotal(body, list.length));
        setRunsPage(page);
        setConnectionFailure(null);
        if (!selectedRunIdRef.current && list[0]?.id != null) {
          setSelectedRunId(list[0].id);
        }
      } catch (err) {
        if (err.connectionFailure) setConnectionFailure(err.message);
        else showAlert("danger", err.message || "Could not load runs.");
      } finally {
        if (!silent) setRunsLoading(false);
      }
    },
    [showAlert]
  );

  const loadRunDetail = useCallback(
    async (id) => {
      if (id == null) {
        setSelectedRun(null);
        return;
      }
      setRunDetailLoading(true);
      try {
        const body = await api.getRunById(id);
        setSelectedRun(parseRun(unwrapObject(body) || body));
      } catch (err) {
        showAlert("danger", err.message || "Could not load run detail.");
      } finally {
        setRunDetailLoading(false);
      }
    },
    [showAlert]
  );

  const loadItems = useCallback(
    async (id, page = 1, outcome = outcomeFilter) => {
      if (id == null) {
        setItems([]);
        setItemsTotal(0);
        return;
      }
      setItemsLoading(true);
      try {
        const body = await api.getRunItems(id, {
          page,
          limit: ITEMS_LIMIT,
          outcome: outcome || undefined,
        });
        const list = unwrapList(body).map(parseItem);
        setItems(list);
        setItemsTotal(unwrapTotal(body, list.length));
        setItemsPage(page);
      } catch (err) {
        showAlert("danger", err.message || "Could not load lead outcomes.");
      } finally {
        setItemsLoading(false);
      }
    },
    [outcomeFilter, showAlert]
  );

  const refreshAll = useCallback(
    async ({ silent } = {}) => {
      await Promise.all([loadConfig({ silent }), loadRuns(runsPage, { silent })]);
      const id = selectedRunIdRef.current;
      if (id != null) {
        await Promise.all([loadRunDetail(id), loadItems(id, itemsPage, outcomeFilter)]);
      }
    },
    [loadConfig, loadRuns, loadRunDetail, loadItems, runsPage, itemsPage, outcomeFilter]
  );

  useEffect(() => {
    refreshAll();
    // initial load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onVis = () => setTabVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    if (!tabVisible) return undefined;
    const t = setInterval(() => {
      refreshAll({ silent: true });
    }, POLL_MS);
    return () => clearInterval(t);
  }, [tabVisible, refreshAll]);

  useEffect(() => {
    if (selectedRunId == null) return;
    setItemsPage(1);
    loadRunDetail(selectedRunId);
    loadItems(selectedRunId, 1, outcomeFilter);
  }, [selectedRunId]); // eslint-disable-line react-hooks/exhaustive-deps

  const askConfirm = (opts) =>
    new Promise((resolve) => {
      setConfirm({
        ...opts,
        onConfirm: async () => {
          setConfirm((c) => (c ? { ...c, busy: true } : c));
          try {
            await opts.onConfirm?.();
            resolve(true);
          } catch (e) {
            showAlert("danger", e.message || "Action failed");
            resolve(false);
          } finally {
            setConfirm(null);
          }
        },
        onCancel: () => {
          setConfirm(null);
          resolve(false);
        },
      });
    });

  async function withBusy(key, fn) {
    if (busyKey) return false;
    setBusyKey(key);
    try {
      await fn();
      return true;
    } catch (e) {
      if (e?.connectionFailure) setConnectionFailure(e.message);
      else showAlert("danger", api.formatApiError(e));
      return false;
    } finally {
      setBusyKey("");
    }
  }

  function currentPayload(enabled) {
    return {
      enabled,
      interval_minutes: Number(intervalMinutes),
      timezone: String(timezone || DEFAULT_TZ).trim(),
    };
  }

  async function saveEnabled(enabled) {
    const err = validateSchedule(intervalMinutes, timezone);
    if (err) {
      setFieldError(err);
      return;
    }
    setFieldError("");
    await withBusy(enabled ? "start" : "stop", async () => {
      await api.putConfig(currentPayload(enabled));
      showAlert(
        "success",
        enabled
          ? "Scheduled sync is enabled. Future runs will write to the destination."
          : "Scheduled sync is paused. Future scheduled runs will not start. A sync already in progress is not cancelled."
      );
      await refreshAll({ silent: true });
    });
  }

  async function handleStart() {
    const err = validateSchedule(intervalMinutes, timezone);
    if (err) {
      setFieldError(err);
      return;
    }
    await askConfirm({
      title: "Enable scheduled sync?",
      message:
        "This turns on the scheduler. When it fires, it will write matching leads to the destination SQL Server. Database credentials stay on the server and are never shown here.",
      confirmLabel: "Enable schedule",
      onConfirm: () => saveEnabled(true),
    });
  }

  async function handleStop() {
    await saveEnabled(false);
  }

  async function handleSaveSettings(e) {
    e.preventDefault();
    const err = validateSchedule(intervalMinutes, timezone);
    if (err) {
      setFieldError(err);
      return;
    }
    setFieldError("");
    const enabled = Boolean(config?.enabled);
    await withBusy("save", async () => {
      await api.putConfig(currentPayload(enabled));
      showAlert("success", "Schedule settings saved.");
      await loadConfig({ silent: true });
    });
  }

  async function handleTest() {
    await withBusy("test", async () => {
      const body = await api.testConnections();
      const msg =
        body?.message ||
        (body?.success === false ? "Connection test failed." : "Source MySQL and destination SQL Server connections succeeded.");
      showAlert("success", msg);
    });
  }

  async function handlePreview() {
    await withBusy("preview", async () => {
      const body = await api.runNow(true);
      const run = parseRun(unwrapObject(body));
      showAlert(
        "success",
        body?.message ||
          `Preview finished${run?.id != null ? ` (run ${run.id})` : ""}. Destination rows were not inserted.`
      );
      if (run?.id != null) setSelectedRunId(run.id);
      await refreshAll({ silent: true });
    });
  }

  async function handleRunNow() {
    await askConfirm({
      title: "Run sync now?",
      message:
        "This starts a real sync and will insert destination rows. There is no cancel-run API, so an in-progress run cannot be stopped from this screen.",
      confirmLabel: "Run sync now",
      danger: true,
      onConfirm: async () => {
        await withBusy("run", async () => {
          const body = await api.runNow(false);
          const run = parseRun(unwrapObject(body));
          showAlert("success", body?.message || `Sync started${run?.id != null ? ` (run ${run.id})` : ""}.`);
          if (run?.id != null) setSelectedRunId(run.id);
          await refreshAll({ silent: true });
        });
      },
    });
  }

  const latestFromHistory = runs[0] || null;
  const latestRun =
    selectedRun && latestFromHistory && String(selectedRun.id) === String(latestFromHistory.id)
      ? selectedRun
      : latestFromHistory;
  const anyBusy = Boolean(busyKey);

  return (
    <MainLayout>
      <div className="pbx4455-page">
        <div className="page-header mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="header-icon" aria-hidden="true">
              <i className="bi bi-arrow-repeat" />
            </div>
            <div>
              <h4 className="mb-0 fw-bold">APNA Clinic PBX 4455 Sync</h4>
              <p className="text-muted mb-0 small">
                Operational dashboard for scheduled lead sync. Credentials are stored only on the server.
              </p>
            </div>
          </div>
        </div>

        {alert ? <Alert {...alert} onClose={() => setAlert(null)} /> : null}
        {connectionFailure ? (
          <Alert type="danger" message={connectionFailure} onClose={() => setConnectionFailure(null)} />
        ) : null}

        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-white border-bottom px-4 py-3">
            <h5 className="mb-0 fw-bold">Current state</h5>
          </div>
          <div className="card-body p-4">
            {configLoading && !config ? (
              <PageLoader label="Loading scheduler config…" />
            ) : (
              <>
                <div className="pbx4455-stat-grid mb-4">
                  <div className="pbx4455-stat">
                    <div className="label">Scheduler</div>
                    <div className="value">
                      <StatusBadge status={config?.enabled ? "enabled" : "paused"} />
                      <span className="visually-hidden">
                        {config?.enabled ? "Enabled" : "Paused"}
                      </span>
                    </div>
                  </div>
                  <div className="pbx4455-stat">
                    <div className="label">Interval</div>
                    <div className="value">
                      {config ? `${config.interval_minutes} minutes` : "—"}
                    </div>
                  </div>
                  <div className="pbx4455-stat">
                    <div className="label">Timezone</div>
                    <div className="value">{config?.timezone || "—"}</div>
                  </div>
                  <div className="pbx4455-stat">
                    <div className="label">Last updated</div>
                    <div className="value">{formatDate(config?.updated_at)}</div>
                  </div>
                </div>

                <form onSubmit={handleSaveSettings}>
                  <div className="row g-3 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label fw-semibold" htmlFor="pbx4455-interval">
                        Interval (minutes)
                      </label>
                      <input
                        id="pbx4455-interval"
                        className={`form-control ${fieldError && fieldError.includes("Interval") ? "is-invalid" : ""}`}
                        type="number"
                        min={1}
                        max={1440}
                        step={1}
                        value={intervalMinutes}
                        onChange={(e) => setIntervalMinutes(e.target.value)}
                        disabled={anyBusy}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" htmlFor="pbx4455-timezone">
                        IANA timezone
                      </label>
                      <input
                        id="pbx4455-timezone"
                        className={`form-control ${fieldError && fieldError.includes("Timezone") ? "is-invalid" : ""}`}
                        list="pbx4455-tz-list"
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                        disabled={anyBusy}
                      />
                      <datalist id="pbx4455-tz-list">
                        {TIMEZONES.map((tz) => (
                          <option key={tz} value={tz} />
                        ))}
                      </datalist>
                    </div>
                    <div className="col-md-5 pbx4455-actions">
                      <button type="submit" className="btn btn-outline-primary" disabled={anyBusy}>
                        {busyKey === "save" ? (
                          <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
                        ) : (
                          <i className="bi bi-save me-1" aria-hidden="true" />
                        )}
                        Save settings
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        disabled={anyBusy || config?.enabled}
                        onClick={handleStart}
                      >
                        {busyKey === "start" ? (
                          <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
                        ) : (
                          <i className="bi bi-play-fill me-1" aria-hidden="true" />
                        )}
                        Start
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={anyBusy || !config?.enabled}
                        onClick={handleStop}
                      >
                        {busyKey === "stop" ? (
                          <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
                        ) : (
                          <i className="bi bi-pause-fill me-1" aria-hidden="true" />
                        )}
                        Stop
                      </button>
                    </div>
                  </div>
                  {fieldError ? (
                    <div className="invalid-feedback d-block mt-2" role="alert">
                      {fieldError}
                    </div>
                  ) : (
                    <p className="text-muted small mt-2 mb-0">
                      Stop pauses future scheduled runs only. It does not cancel a sync that is already running.
                    </p>
                  )}
                </form>

                <hr className="my-4" />

                <div className="pbx4455-actions">
                  <button type="button" className="btn btn-outline-dark" disabled={anyBusy} onClick={handleTest}>
                    {busyKey === "test" ? (
                      <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
                    ) : (
                      <i className="bi bi-plug me-1" aria-hidden="true" />
                    )}
                    Test connections
                  </button>
                  <button type="button" className="btn btn-outline-info" disabled={anyBusy} onClick={handlePreview}>
                    {busyKey === "preview" ? (
                      <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
                    ) : (
                      <i className="bi bi-eye me-1" aria-hidden="true" />
                    )}
                    Preview sync
                  </button>
                  <button type="button" className="btn btn-danger" disabled={anyBusy} onClick={handleRunNow}>
                    {busyKey === "run" ? (
                      <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
                    ) : (
                      <i className="bi bi-lightning-fill me-1" aria-hidden="true" />
                    )}
                    Run sync now
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-white border-bottom px-4 py-3">
            <h5 className="mb-0 fw-bold">Latest run</h5>
          </div>
          <div className="card-body p-4">
            {runDetailLoading && !latestRun ? (
              <PageLoader label="Loading latest run…" />
            ) : !latestRun ? (
              <EmptyState title="No runs yet" hint="Preview or run a sync to see results here." />
            ) : (
              <div className="pbx4455-stat-grid">
                <div className="pbx4455-stat">
                  <div className="label">Status</div>
                  <div className="value">
                    <StatusBadge status={latestRun.status} />
                  </div>
                </div>
                <div className="pbx4455-stat">
                  <div className="label">Source</div>
                  <div className="value">{countLabel(latestRun.source_count)}</div>
                </div>
                <div className="pbx4455-stat">
                  <div className="label">Matched</div>
                  <div className="value">{countLabel(latestRun.matched)}</div>
                </div>
                <div className="pbx4455-stat">
                  <div className="label">Inserted</div>
                  <div className="value">{countLabel(latestRun.inserted)}</div>
                </div>
                <div className="pbx4455-stat">
                  <div className="label">Skipped</div>
                  <div className="value">{countLabel(latestRun.skipped)}</div>
                </div>
                <div className="pbx4455-stat">
                  <div className="label">Failed</div>
                  <div className="value">{countLabel(latestRun.failed)}</div>
                </div>
                <div className="pbx4455-stat">
                  <div className="label">Started</div>
                  <div className="value">{formatDate(latestRun.started_at)}</div>
                </div>
                <div className="pbx4455-stat">
                  <div className="label">Finished</div>
                  <div className="value">{formatDate(latestRun.finished_at)}</div>
                </div>
                {latestRun.error ? (
                  <div className="pbx4455-stat" style={{ gridColumn: "1 / -1" }}>
                    <div className="label">Run error</div>
                    <div className="value text-danger pbx4455-error-text">{String(latestRun.error)}</div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-white border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0 fw-bold">Run history</h5>
            <span className="small text-muted">Select a row to load lead outcomes</span>
          </div>
          <div className="card-body p-4">
            {runsLoading && runs.length === 0 ? (
              <PageLoader label="Loading run history…" />
            ) : runs.length === 0 ? (
              <EmptyState icon="bi-clock-history" title="No history" hint="Runs will appear after a preview or sync." />
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-sm align-middle pbx4455-table">
                    <thead>
                      <tr>
                        <th>Run ID</th>
                        <th>Trigger</th>
                        <th>Dry run</th>
                        <th>Status</th>
                        <th>Source</th>
                        <th>Matched</th>
                        <th>Inserted</th>
                        <th>Skipped</th>
                        <th>Failed</th>
                        <th>Started</th>
                        <th>Finished</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map((run) => {
                        const selected = String(run.id) === String(selectedRunId);
                        return (
                          <tr
                            key={run.id}
                            className={`pbx4455-row-click ${selected ? "is-selected" : ""}`}
                            tabIndex={0}
                            aria-selected={selected}
                            onClick={() => setSelectedRunId(run.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedRunId(run.id);
                              }
                            }}
                          >
                            <td>{countLabel(run.id)}</td>
                            <td>{run.trigger || "—"}</td>
                            <td>
                              {run.dry_run ? (
                                <span className="badge-status badge-status-dry">
                                  <i className="bi bi-eye me-1" aria-hidden="true" />
                                  Preview
                                </span>
                              ) : (
                                <span>Write</span>
                              )}
                            </td>
                            <td>
                              <StatusBadge status={run.status} />
                            </td>
                            <td>{countLabel(run.source_count)}</td>
                            <td>{countLabel(run.matched)}</td>
                            <td>{countLabel(run.inserted)}</td>
                            <td>{countLabel(run.skipped)}</td>
                            <td>{countLabel(run.failed)}</td>
                            <td>{formatDate(run.started_at)}</td>
                            <td>{formatDate(run.finished_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={runsPage}
                  total={runsTotal}
                  limit={RUNS_LIMIT}
                  disabled={runsLoading}
                  onPage={(p) => loadRuns(p)}
                />
              </>
            )}
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-bottom px-4 py-3">
            <h5 className="mb-0 fw-bold">
              Lead outcomes{selectedRunId != null ? ` · run ${selectedRunId}` : ""}
            </h5>
          </div>
          <div className="card-body p-4">
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold" htmlFor="pbx4455-outcome">
                  Outcome filter
                </label>
                <select
                  id="pbx4455-outcome"
                  className="form-select"
                  value={outcomeFilter}
                  disabled={itemsLoading || selectedRunId == null}
                  onChange={(e) => {
                    const next = e.target.value;
                    setOutcomeFilter(next);
                    setItemsPage(1);
                    loadItems(selectedRunId, 1, next);
                  }}
                >
                  <option value="">All</option>
                  <option value="failed">Failed</option>
                  <option value="inserted">Inserted</option>
                  <option value="skipped">Skipped</option>
                  <option value="matched">Matched</option>
                </select>
              </div>
            </div>

            {selectedRunId == null ? (
              <EmptyState title="Select a run" hint="Choose a run from history to inspect lead outcomes." />
            ) : itemsLoading && items.length === 0 ? (
              <PageLoader label="Loading lead outcomes…" />
            ) : items.length === 0 ? (
              <EmptyState
                icon="bi-list-check"
                title="No lead outcomes"
                hint={outcomeFilter ? `No rows with outcome “${outcomeFilter}”.` : "This run has no item rows."}
              />
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-sm align-middle pbx4455-table">
                    <thead>
                      <tr>
                        <th>Lead ID</th>
                        <th>Phone</th>
                        <th>Outcome</th>
                        <th>Destination ID</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={`${item.lead_id}-${idx}`}>
                          <td>{item.lead_id}</td>
                          <td>{item.phone}</td>
                          <td>
                            <StatusBadge status={item.outcome} />
                          </td>
                          <td>{item.destination_id}</td>
                          <td className="pbx4455-error-text">{item.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={itemsPage}
                  total={itemsTotal}
                  limit={ITEMS_LIMIT}
                  disabled={itemsLoading}
                  onPage={(p) => loadItems(selectedRunId, p, outcomeFilter)}
                />
              </>
            )}
          </div>
        </div>

        <ConfirmModal
          open={Boolean(confirm)}
          title={confirm?.title}
          message={confirm?.message}
          confirmLabel={confirm?.confirmLabel}
          danger={confirm?.danger}
          busy={confirm?.busy}
          onConfirm={confirm?.onConfirm}
          onCancel={confirm?.onCancel}
        />
      </div>
    </MainLayout>
  );
}
